import { useState } from 'react';
import { toast } from 'sonner';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { AdminUser } from '@/hooks/admin/useAdminUsers';
import { useAdjustCredits } from '@/hooks/admin/useAdjustCredits';

interface CreditAdjustmentModalProps {
    user: AdminUser;
    onClose: () => void;
}

export function CreditAdjustmentModal({ user, onClose }: CreditAdjustmentModalProps) {
    const [action, setAction] = useState<'add' | 'deduct'>('add');
    const [amount, setAmount] = useState('');
    const [description, setDescription] = useState('');

    const adjustCredits = useAdjustCredits();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const amountNum = parseInt(amount, 10);

        if (isNaN(amountNum) || amountNum <= 0) {
            toast.error('Please enter a valid positive amount');
            return;
        }

        if (!description.trim()) {
            toast.error('Description is required');
            return;
        }

        try {
            const result = await adjustCredits.mutateAsync({
                userId: user.id,
                action,
                amount: amountNum,
                description: description.trim(),
            });

            toast.success(result.message);
            onClose();
        } catch {
            toast.error('Failed to adjust credits. Please try again.');
        }
    };

    return (
        <Dialog open={true} onClose={onClose} title="Adjust Credits" size="md">
            <form onSubmit={handleSubmit} className="space-y-4">
                {/* User info */}
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-700/50">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        Adjusting credits for:
                    </p>
                    <p className="font-semibold text-gray-900 dark:text-white">
                        {user.email}
                    </p>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                        Current balance:{' '}
                        <span className="font-semibold">{user.creditBalance} credits</span>
                    </p>
                </div>

                {/* Action */}
                <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Action
                    </label>
                    <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="radio"
                                name="action"
                                value="add"
                                checked={action === 'add'}
                                onChange={() => setAction('add')}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">Add Credits</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="radio"
                                name="action"
                                value="deduct"
                                checked={action === 'deduct'}
                                onChange={() => setAction('deduct')}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">Deduct Credits</span>
                        </label>
                    </div>
                </div>

                {/* Amount */}
                <div>
                    <label
                        htmlFor="amount"
                        className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                        Amount
                    </label>
                    <Input
                        id="amount"
                        type="number"
                        min="1"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="Enter amount"
                        required
                    />
                </div>

                {/* Description */}
                <div>
                    <label
                        htmlFor="description"
                        className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                        Description (required)
                    </label>
                    <textarea
                        id="description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Reason for adjustment..."
                        required
                        rows={3}
                        className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-hidden focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                    />
                </div>

                {/* Preview */}
                {amount && parseInt(amount, 10) > 0 && (
                    <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-600">
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                            New balance will be:{' '}
                            <span className="font-semibold">
                                {action === 'add'
                                    ? user.creditBalance + parseInt(amount, 10)
                                    : Math.max(0, user.creditBalance - parseInt(amount, 10))}{' '}
                                credits
                            </span>
                        </p>
                        {action === 'deduct' && parseInt(amount, 10) > user.creditBalance && (
                            <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                                Warning: This will result in a negative balance (which is not allowed).
                            </p>
                        )}
                    </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        isLoading={adjustCredits.isPending}
                        disabled={
                            !amount ||
                            parseInt(amount, 10) <= 0 ||
                            !description.trim() ||
                            (action === 'deduct' && parseInt(amount, 10) > user.creditBalance)
                        }
                    >
                        {action === 'add' ? 'Add Credits' : 'Deduct Credits'}
                    </Button>
                </div>
            </form>
        </Dialog>
    );
}
